import { ReadonlyVec2, vec4 } from 'gl-matrix';
import { gl } from '~/engine/Engine';
import { FrameBufferObject } from '~/engine/core/utils/FrameBufferObject';
import { Grid } from '~/engine/data/common/Grid';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import {
    DrawActionState,
    sampledImageOf,
} from '~/engine/tool/actions/DrawActionState';
import { DrawObject } from '~/engine/tool/actions/DrawObject';
import { TextureView } from '~/engine/view/TextureView';
import { ViewManager } from '~/engine/view/ViewManager';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { AdaptiveBrushStamp } from './AdaptiveBrushStamp';
import {
    BrushMode,
    BrushStamp,
    BrushStampContext,
    BrushStrokeOptions,
} from './BrushStamp';
import {
    ConstraintMasks,
    applyConstraintMasks,
    buildConstraintMasks,
    deleteConstraintMasks,
} from './constraintClipping';
import { StaticBrushStamp } from './StaticBrushStamp';
import { StrokeMode } from '~/state/atoms/toolsAtoms';

/** The eraser, as the clinician sees it named in the toolbar. */
const ERASER_ACTION_NAME = 'Eraser';

/** The stroke mode as the tray shows it. */
const STROKE_MODE_NAMES: Record<StrokeMode, string> = {
    '2d': '2D',
    '3d': '3D',
};

function createStamp(mode: BrushMode, context: BrushStampContext): BrushStamp {
    switch (mode) {
        case 'static':
            return new StaticBrushStamp(context);
        case 'smart':
            return new AdaptiveBrushStamp(context);
    }
}

/**
 * One brush stroke, from press to release: a {@link DrawObject} draw buffer
 * shown on every view displaying the image, the {@link BrushStamp} left at
 * every cursor position, and the live preview clipped to the stroke's
 * {@link DrawingConstraints}. The first stamp is drawn on construction.
 *
 * The draw buffer is published on the stroked view's environment, so a
 * stroke reaching slices the stroked view does not show can be checked on
 * the views that do before it is committed, whichever views exist while
 * it is in progress.
 */
export class BrushStroke extends DrawActionState {
    private readonly stamp: BrushStamp;

    /**
     * {@link DrawObject} holding what the stroke has drawn so far, on the
     * part of the image grid the view shows.
     */
    private readonly drawObject: DrawObject;

    /**
     * Framebuffer the {@link drawObject}'s layers are attached to, one at a
     * time, while a stamp is drawn onto them.
     */
    private readonly fbo: FrameBufferObject;

    /**
     * Constraint masks on the fixed preview grid, built on the first
     * preview clip and reused for the rest of the stroke.
     */
    private previewMasks: ConstraintMasks | null | undefined;

    private readonly strokeMode: StrokeMode;

    /**
     * @param view View the stroke is drawn on; for a 2D stroke its plane
     *     must follow the image slices
     * @param target Structure the stroke is committed onto
     * @param image Image whose grid the stroke is drawn on
     * @param coordinates Client pixel coordinates of the first stamp
     * @throws Error when a 2D stroke's view follows no image slice
     */
    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        image: ImageObject,
        coordinates: ReadonlyVec2,
        options: BrushStrokeOptions
    ) {
        super(view, target, image, options.erase, options.constraints);
        this.strokeMode = options.strokeMode;

        const sliceAxis = image.grid.axisAlong(view.viewDirection);
        if (options.strokeMode === '2d' && sliceAxis === undefined) {
            throw new Error(
                'A 2D brush stroke needs a view whose plane follows the image slices.'
            );
        }
        const margin = ContourTexture.DEFAULT_MARGIN;
        const stamp = createStamp(options.mode, {
            view,
            target,
            image,
            sampledImage: sampledImageOf(view, image),
            diameter: options.diameter,
            margin,
            sliceAxis,
            strokeMode: options.strokeMode,
        });
        // The draw buffer covers the displayed rectangle, extruded along
        // the view normal as far as the brush reaches: one slice for a 2D
        // stroke, the ball with its margin for a 3D stroke.
        const halfDepthMm =
            options.strokeMode === '3d'
                ? 0.5 * options.diameter + margin
                : undefined;
        let drawObject: DrawObject | undefined;
        let fbo: FrameBufferObject;
        try {
            drawObject = new DrawObject(
                image,
                view.getPatientSpaceBoundingBox(halfDepthMm),
                vec4.fromValues(
                    target.color[0],
                    target.color[1],
                    target.color[2],
                    1
                )
            );
            fbo = new FrameBufferObject(drawObject.planarPixelSize);
        } catch (error) {
            drawObject?.delete();
            stamp.delete();
            throw error;
        }
        this.stamp = stamp;
        this.drawObject = drawObject;
        this.fbo = fbo;
        view.environment.addCustomObject(drawObject);

        try {
            this.onMouseMove(coordinates, 0, view);
        } catch (error) {
            this.clearMemory();
            throw error;
        }
    }

    /**
     * How the undo history names this stroke: the tool the clinician
     * picked and the mode it ran in, e.g. "Static brush 3D". Erasing is
     * one tool in the toolbar whichever brush shapes it, so every erasing
     * stroke carries the eraser's name. The mode is named because a 3D
     * stroke reaches slices a 2D one cannot, which is what a reader of the
     * history needs to know about a step.
     */
    public get actionName(): string {
        const tool = this._erase ? ERASER_ACTION_NAME : this.stamp.label;
        return `${tool} ${STROKE_MODE_NAMES[this.strokeMode]}`;
    }

    /**
     * Stamp the brush at the cursor, clip the preview to the constraints and
     * repaint the views showing the stroke. A move on any other view is not
     * handled.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        _buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if (view !== this.view) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        this.stamp.stampAt(
            this.view.clientToPatientCoordinates(coordinates),
            (shape) => this.drawIntoReachedLayers(shape)
        );
        this.clipPreviewToConstraints();
        ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT, this.drawObject);
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Commit the stroke onto the target structure, release it and redraw
     * the structure's contours.
     */
    public commit(): EventHandlerResult {
        try {
            this.applyBoolean();
        } finally {
            this.clearMemory();
        }
        ViewManager.invalidate(
            LayerBit.CONTOUR_LAYER_BIT,
            this.targetStructure.boundingBox
        );
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    /**
     * Release the stroke without modifying the target structure, and
     * repaint every view so nothing of its preview stays on screen. The
     * preview may have been shown on any view of the environment, so no
     * volume narrows the repaint.
     */
    public clearMemory(): void {
        this.view.environment.removeCustomObject(this.drawObject);
        ViewManager.invalidate(LayerBit.CONTOUR_LAYER_BIT);
        this.drawObject.delete();
        this.fbo.delete();
        this.stamp.delete();
        if (this.previewMasks) {
            deleteConstraintMasks(this.previewMasks);
        }
        this.previewMasks = undefined;
    }

    protected get contourTexture(): ContourTexture | undefined {
        return this.drawObject.toContourTexture();
    }

    /**
     * Draw a shape into the {@link drawObject}, keeping per voxel the larger
     * of the drawn and the existing value. Only the layers the shape can
     * reach are visited. The caller's shader must be started.
     */
    private drawIntoReachedLayers(shape: Grid): void {
        const grid = this.drawObject.grid;
        let minLayer = Infinity;
        let maxLayer = -Infinity;
        for (const corner of shape.getVerticesWithTexture()) {
            const layer = grid.toVoxelSpace(corner.coordinates)[2];
            minLayer = Math.min(minLayer, layer);
            maxLayer = Math.max(maxLayer, layer);
        }
        const first = Math.max(0, Math.floor(minLayer));
        const last = Math.min(grid.sizeZ - 1, Math.floor(maxLayer));

        this.fbo.bind();
        gl.viewport(0, 0, this.fbo.width, this.fbo.height);
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.MAX);
        gl.blendFunc(gl.ONE, gl.ONE);
        try {
            for (let z = first; z <= last; ++z) {
                this.fbo.createTextureLayer(
                    this.drawObject.signedDistanceField.texture,
                    z
                );

                const vao = shape.crossSection(grid.worldToLayerProjection(z));
                if (vao) {
                    try {
                        vao.bind(0, 1);
                        gl.drawElements(
                            gl.TRIANGLES,
                            vao.indexCount,
                            gl.UNSIGNED_INT,
                            0
                        );
                    } finally {
                        vao.unbind(0, 1);
                        vao.delete();
                    }
                }
            }
        } finally {
            gl.blendEquation(gl.FUNC_ADD);
            gl.disable(gl.BLEND);
            this.fbo.unbind();
        }
    }

    /**
     * Clip the live preview to the stroke's constraints in place on the
     * draw buffer, so the preview "sticks" to the boundary it must not cross
     * (Inside → intersect, Outside → subtract) and matches what will be
     * committed (both use the same {@link constraintClipping} primitives).
     * The masks are built once per stroke, so the per-frame cost is a fixed
     * number of passes regardless of how many protected structures are
     * selected.
     */
    private clipPreviewToConstraints(): void {
        if (!this._constraints) {
            return;
        }
        const preview = this.drawObject.contourTexture;
        if (!preview) {
            return;
        }
        if (this.previewMasks === undefined) {
            this.previewMasks = buildConstraintMasks(
                preview,
                this._constraints,
                this.targetImage,
                this.targetStructure.roiId
            );
        }
        if (this.previewMasks) {
            applyConstraintMasks(preview, this.previewMasks);
        }
    }
}
